import React, { useMemo } from 'react';

import { useTheme } from '@material-ui/core/styles';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import useReduxState from '@portal/hooks/useReduxState';

import manWalkingLottie from '~/assets/lotties/man-walking.json';

type ColorMap = Record<string, string>;

type LayerColorMaps = {
  default?: ColorMap;
  byLayerInd?: Record<number, ColorMap>;
};

const readCssColor = (name: string): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || undefined;
};

const channelHex = (n: number) =>
  Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, '0');

const hexToLottieColor = (hex: string): [number, number, number, number] => {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  return [r, g, b, 1];
};

const lottieColorToHex = (color: readonly number[]): string =>
  `#${channelHex(color[0] * 255)}${channelHex(color[1] * 255)}${channelHex(
    color[2] * 255
  )}`;

const darkenHex = (hex: string, factor: number): string => {
  const normalized = hex.replace('#', '');
  const r = (parseInt(normalized.slice(0, 2), 16) || 0) * factor;
  const g = (parseInt(normalized.slice(2, 4), 16) || 0) * factor;
  const b = (parseInt(normalized.slice(4, 6), 16) || 0) * factor;
  return `#${channelHex(r)}${channelHex(g)}${channelHex(b)}`;
};

const normalizeMap = (map?: ColorMap): ColorMap | undefined => {
  if (!map) return undefined;
  const out: ColorMap = {};
  for (const [from, to] of Object.entries(map)) {
    out[from.toLowerCase()] = to.toLowerCase();
  }
  return out;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'number');

const recolorLottie = (
  source: Record<string, unknown>,
  maps: LayerColorMaps
): Record<string, unknown> => {
  const defaultMap = normalizeMap(maps.default);
  const byLayerInd: Record<string, ColorMap | undefined> = {};
  for (const [ind, map] of Object.entries(maps.byLayerInd ?? {})) {
    byLayerInd[ind] = normalizeMap(map);
  }

  const walkValue = (
    value: unknown,
    currentLayerInd: number | null
  ): unknown => {
    if (Array.isArray(value)) {
      return value.map((v) => walkValue(v, currentLayerInd));
    }
    if (isPlainObject(value)) {
      return walkObject(value, currentLayerInd);
    }
    return value;
  };

  const walkObject = (
    obj: Record<string, unknown>,
    currentLayerInd: number | null
  ): Record<string, unknown> => {
    const { ind, ty } = obj;
    const layerInd =
      typeof ind === 'number' && typeof ty === 'number' ? ind : currentLayerInd;

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      if (key === 'c' && isPlainObject(val) && isNumberArray(val.k)) {
        const currentHex = lottieColorToHex(val.k);
        const layerMap =
          layerInd !== null ? byLayerInd[String(layerInd)] : undefined;
        const map = layerMap ?? defaultMap;
        const target = map?.[currentHex];
        if (target) {
          result[key] = { ...val, k: hexToLottieColor(target) };
          continue;
        }
      }
      result[key] = walkValue(val, layerInd);
    }
    return result;
  };

  return walkObject(source, null);
};

const PANTS_LAYER_INDS = [6, 7, 9, 10];
const SHOE_LAYER_INDS = [8, 11];

const buildLayerMaps = (
  primary: string,
  primaryDark: string,
  secondary: string
): LayerColorMaps => {
  const pantsMap: ColorMap = {
    '#2a2876': primary,
    '#2a2976': primary,
    '#161551': primary,
  };

  const shoesMap: ColorMap = {
    '#161551': secondary,
    '#171651': secondary,
  };

  const byLayerInd: Record<number, ColorMap> = {};
  PANTS_LAYER_INDS.forEach((ind) => {
    byLayerInd[ind] = pantsMap;
  });
  SHOE_LAYER_INDS.forEach((ind) => {
    byLayerInd[ind] = shoesMap;
  });

  return {
    default: {
      '#161551': primary,
      '#171651': primary,
      '#0b0b29': primaryDark,
      '#0f1029': primaryDark,
    },
    byLayerInd,
  };
};

type WalkingManAnimationProps = {
  className?: string;
  style?: React.CSSProperties;
  autoplay?: boolean;
  loop?: boolean;
};

const WalkingManAnimation = ({
  className,
  style,
  autoplay = true,
  loop = true,
}: WalkingManAnimationProps) => {
  const {
    theme: { currentTheme },
  } = useReduxState();
  const muiTheme = useTheme();

  const themedSource = useMemo(() => {
    const primary =
      currentTheme?.primaryColor ?? readCssColor('--brand-primary');
    const secondary =
      muiTheme.palette.secondary?.main ??
      readCssColor('--brand-secondary') ??
      primary;
    if (!primary || !secondary) return manWalkingLottie;
    const primaryDark = darkenHex(primary, 0.5);
    return recolorLottie(
      manWalkingLottie,
      buildLayerMaps(primary, primaryDark, secondary)
    );
  }, [currentTheme?.primaryColor, muiTheme.palette.secondary?.main]);

  return (
    <DotLottieReact
      data={themedSource}
      autoplay={autoplay}
      loop={loop}
      className={className}
      style={style}
    />
  );
};

export default WalkingManAnimation;
